import React from 'react';
import {
  Box,
  CardMedia,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from './ScrollableDialog';
import ConfirmDialogActions, {
  confirmDialogActionsSx,
  confirmDialogActionsWithSecondarySx,
} from './ConfirmDialogActions';

const destructiveActionButtonSx = {
  background: 'linear-gradient(135deg, #c62828 0%, #f44336 100%)',
  boxShadow: '0 10px 24px rgba(198, 40, 40, 0.18)',
  '&:hover': {
    background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)',
  },
};

const successActionButtonSx = {
  background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
  boxShadow: '0 10px 24px rgba(46, 125, 50, 0.22)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
  },
};

const confirmActionButtonSx = {
  error: destructiveActionButtonSx,
  success: successActionButtonSx,
};

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  onSecondaryConfirm,
  title,
  message,
  confirmText,
  secondaryConfirmText,
  cancelText,
  confirmColor = 'error',
  imageUrl,
  loading = false,
  confirmDisabled = false,
  closeOnConfirm = true,
  children,
}) => {
  const { t } = useTranslation();
  const hasSecondaryConfirm = Boolean(secondaryConfirmText && onSecondaryConfirm);

  const runConfirm = (action) => {
    action();
    if (closeOnConfirm) {
      onClose();
    }
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      title={title || t('common.confirm')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={hasSecondaryConfirm ? confirmDialogActionsWithSecondarySx : confirmDialogActionsSx}
      actions={(
        <ConfirmDialogActions
          onCancel={onClose}
          cancelText={cancelText || t('common.cancel')}
          onConfirm={() => runConfirm(onConfirm)}
          confirmText={confirmText || t('common.confirm')}
          onSecondaryConfirm={hasSecondaryConfirm ? () => runConfirm(onSecondaryConfirm) : undefined}
          secondaryConfirmText={secondaryConfirmText}
          loading={loading}
          confirmDisabled={confirmDisabled}
          confirmButtonSx={confirmActionButtonSx[confirmColor]}
        />
      )}
    >
      {imageUrl && (
        <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <CardMedia
            component="img"
            image={imageUrl}
            alt="Preview"
            sx={{
              width: '100%',
              maxHeight: 300,
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
      {message ? (
        <Typography variant="body1">
          {message}
        </Typography>
      ) : null}
      {children}
    </ScrollableDialog>
  );
};

export default ConfirmDialog;
